
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
    Application, ApplicationStatus, Company, ComplianceData, ComplianceStatus, EMoneyLicenseIssuer, UBOProvisioningChoice
} from '../models/data-model';
import { CountryService } from './country.service';

export enum REGISTRATION_STEP_ID {
    SIGNUP = "SIGNUP",
    COMPANY_SEARCH = "COMPANY SEARCH",
    COMPANY_IDENTIFIERS = "COMPANY IDENTIFIERS",
    REGISTERED_ADDRESS = "REGISTERED ADDRESS",
    TRADING_ADDRESS = "TRADING ADDRESS",
    SALES_CHANNEL = "SALES CHANNEL",
    INTERNATIONAL_ACTIVITY = "INTERNATIONAL ACTIVITY",
    BUSINESS_DESCRIPTION = "BUSINESS DESCRIPTION",
    DIRECTOR = "LEGAL REPRESENTATIVE",
    ADMIN = "ACCOUNT ADMIN",
    UBO_CHOICE = "BENEFICIAL OWNER CHOICE",
    UBO_DETAILS = "BENEFICIAL OWNER DETAILS",
    USE_CASE = "USE CASE",
    NUMBER_OF_CARDS = "NUMBER OF CARDS",
    MONTHLY_SPEND = "MONTHLY SPEND",
    CASH_WITHDRAWAL = "CASH WITHDRAWAL",
    EXTERNAL_DEPOSIT = "EXTERNAL DEPOSIT",
    TERMS = "TERMS AND CONDITIONS",
    LAST = "LAST"
}

export type MacroStep = "WELCOME" | "COMPANY" | "LEGAL_REP" | "ACCOUNT" | "UBOCHOICE" | "UBODETAILS" | "EXPECTED" | "TERMS" | "LAST";

export interface RegistrationStep {
    id: REGISTRATION_STEP_ID;
    macroStep: MacroStep;
    linkRoute?: () => string;
    active: boolean;
    isEnabled: () => boolean;
    hideFromNavigationBar?: boolean;
}

export enum NavigationDirection {
    BACKWARD = 0,
    FORWARD = 1
}

@Injectable()
export class StepService {

    private readonly registrationStepsOrder: RegistrationStep[] = [];
    private completedSteps: REGISTRATION_STEP_ID[] = [];
    private lastNavigationDirection: NavigationDirection = NavigationDirection.FORWARD;
    private application: Application;
    private complianceData: ComplianceData;
    private companyInput: 'search' | 'manual';
    private currentUrl: string;

    navBarActivationObservable: Subject<boolean> = new BehaviorSubject<boolean>(false);
    stepChanged: Subject<RegistrationStep> = new Subject<RegistrationStep>();

    private readonly signupStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.SIGNUP,
        macroStep: 'WELCOME',
        linkRoute: () => '/registration/welcome/signup',
        active: false,
        isEnabled: () => true
    };
    private readonly companySearchStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.COMPANY_SEARCH,
        macroStep: 'COMPANY',
        linkRoute: () => '/registration/company/company-search',
        active: false,
        isEnabled: () => {
            if (!this.isCompanySearchAvailable()) {
                return false;
            }
            if (this.companyInput) {
                return this.companyInput === 'search';
            }
            const searchResultId = this.application?.company?.kybProviderId;
            const companyIsEmpty = !this.hasCompanyInfo() && !this.hasRegisteredAddress();
            return !!searchResultId || companyIsEmpty;
        }
    };
    private readonly companyIdentifiersStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.COMPANY_IDENTIFIERS,
        macroStep: 'COMPANY',
        linkRoute: () => '/registration/company/info',
        active: false,
        isEnabled: () => {
            const searchResultId = this.application?.company?.kybProviderId;
            return !searchResultId || !this.hasCompanyInfo();
        }
    };
    private readonly registeredAddressStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.REGISTERED_ADDRESS,
        macroStep: 'COMPANY',
        linkRoute: () => '/registration/company/registered-address',
        active: false,
        isEnabled: () => {
            const searchResultId = this.application?.company?.kybProviderId;
            return !searchResultId || !this.hasCompanyInfo() || !this.hasRegisteredAddress();
        }
    };
    private readonly tradingAddressStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.TRADING_ADDRESS,
        macroStep: 'COMPANY',
        linkRoute: () => '/registration/company/trading-address',
        active: false,
        isEnabled: () => true
    };
    private readonly salesChannelStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.SALES_CHANNEL,
        macroStep: 'COMPANY',
        linkRoute: () => '/registration/company/sales-channel',
        active: false,
        isEnabled: () => true
    };
    private readonly internationalActivityStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.INTERNATIONAL_ACTIVITY,
        macroStep: 'COMPANY',
        linkRoute: () => '/registration/company/international-activity',
        active: false,
        isEnabled: () => true
    };
    private readonly businessDescriptionStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.BUSINESS_DESCRIPTION,
        macroStep: 'COMPANY',
        linkRoute: () => '/registration/company/business-description',
        active: false,
        isEnabled: () => true
    };
    private readonly directorStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.DIRECTOR,
        macroStep: 'LEGAL_REP',
        linkRoute: () => '/registration/director',
        active: false,
        isEnabled: () => true
    };
    private readonly adminStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.ADMIN,
        macroStep: 'ACCOUNT',
        linkRoute: () => '/registration/account',
        active: false,
        isEnabled: () => {
            return true;
        }
    };
    private readonly uboChoiceStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.UBO_CHOICE,
        macroStep: 'UBOCHOICE',
        linkRoute: () => '/registration/beneficial-owners',
        active: false,
        isEnabled: () => this.isUboChoiceStepEnabled(this.application?.company?.eMoneyLicenseIssuer)
    };
    private readonly uboDetailsStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.UBO_DETAILS,
        macroStep: 'UBODETAILS',
        linkRoute: () => '/registration/beneficial-owners-details',
        active: false,
        isEnabled: () => this.isUboDetailsStepEnabled(this.uboChoiceStep.isEnabled(), this.application?.uboProvisioningChoice)
    };
    private readonly useCaseStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.USE_CASE,
        macroStep: 'EXPECTED',
        linkRoute: () => '/registration/expected-activity/use-case',
        active: false,
        isEnabled: () => true
    };
    private readonly numberOfCardsStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.NUMBER_OF_CARDS,
        macroStep: 'EXPECTED',
        linkRoute: () => '/registration/expected-activity/number-of-cards',
        active: false,
        isEnabled: () => true
    };
    private readonly monthlySpendStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.MONTHLY_SPEND,
        macroStep: 'EXPECTED',
        linkRoute: () => '/registration/expected-activity/monthly-spend',
        active: false,
        isEnabled: () => true
    };
    private readonly cashWithdrawalStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.CASH_WITHDRAWAL,
        macroStep: 'EXPECTED',
        linkRoute: () => '/registration/expected-activity/cash-withdrawal',
        active: false,
        isEnabled: () => true
    };
    private readonly externalDepositStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.EXTERNAL_DEPOSIT,
        macroStep: 'EXPECTED',
        linkRoute: () => '/registration/expected-activity/external-deposit',
        active: false,
        isEnabled: () => true
    };
    private readonly termsStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.TERMS,
        macroStep: 'TERMS',
        linkRoute: () => '/registration/terms-conditions',
        active: false,
        isEnabled: () => true
    };
    private readonly lastStep: RegistrationStep = {
        id: REGISTRATION_STEP_ID.LAST,
        macroStep: 'LAST',
        linkRoute: () => {
            let route = "/static/thank-you";
            if (this.complianceData?.status === ComplianceStatus.IDV_REQUIRED) {
                route = this.complianceData.idvData?.length > 0 ? "/registration/verify-your-identity" : "/static/thank-you";
            } else if (this.complianceData?.status === ComplianceStatus.DOTFILE_KYB_REQUIRED) {
                route = "/registration/verification-portal";
            }
            return route;
        },
        active: false,
        isEnabled: () => true,
        hideFromNavigationBar: true
    };

    constructor(
        private readonly router: Router,
        private readonly logger: NGXLogger,
        private readonly countryService: CountryService
    ) {
        // All possible steps in order
        this.registrationStepsOrder = [
            this.signupStep,
            this.companySearchStep,
            this.companyIdentifiersStep,
            this.registeredAddressStep,
            this.tradingAddressStep,
            this.salesChannelStep,
            this.internationalActivityStep,
            this.businessDescriptionStep,
            this.directorStep,
            this.adminStep,
            this.uboChoiceStep,
            this.uboDetailsStep,
            this.useCaseStep,
            this.numberOfCardsStep,
            this.monthlySpendStep,
            this.cashWithdrawalStep,
            this.externalDepositStep,
            this.termsStep,
            this.lastStep
        ];

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            this.currentUrl = event.url;
            const activeStep = this.registrationFlow.find(step => step.active);
            if (activeStep) {
                this.stepChanged.next(activeStep);
            }
        });
    }

    isUboChoiceStepEnabled(eMoneyLicenseIssuer: EMoneyLicenseIssuer): boolean {
        return eMoneyLicenseIssuer !== 'SFS-UK';
    }

    isUboDetailsStepEnabled(isUboChoiceStepEnabled: boolean, uboProvisioningChoice: UBOProvisioningChoice): boolean {
        return isUboChoiceStepEnabled && !([UBOProvisioningChoice.Email, UBOProvisioningChoice.None].includes(uboProvisioningChoice));
    }

    get registrationFlow(): RegistrationStep[] {
        const flow = this.registrationStepsOrder.filter(s => s.isEnabled());
        for (let step of flow) {
            step.active = false
        }
        if (this.currentUrl) {
            const activeStep = flow.find(step => step.linkRoute() === this.currentUrl);
            if (activeStep) {
                activeStep.active = true;
            }
        }
        return flow;
    }

    setApplication(application: Application) {
        this.application = application;
        this.setCompany(application.company);
    }

    setCompany(company: Company) {
        this.application.company = company;
    }

    setComplianceData(complianceData: ComplianceData) {
        this.complianceData = complianceData;
    }

    setCompanyInput(companyInput: 'search' | 'manual') {
        this.companyInput = companyInput;
    }

    hasCompanyInfo(): boolean {
        const company = this.application?.company;
        if (!company) {
            return false;
        }
        const requiredIdentifiers = [
            company?.registrationNumber,
            company?.registeredName
        ];
        return requiredIdentifiers.every((fieldValue) => {
            return fieldValue && fieldValue !== "";
        });
    }

    hasRegisteredAddress(): boolean {
        const company = this.application?.company;
        if (!company) {
            return false;
        }
        const requiredAddressFields = [
            company?.registeredAddress?.line1,
            company?.registeredAddress?.postalCode,
            company?.registeredAddress?.city
        ];
        return requiredAddressFields.every((fieldValue) => {
            return fieldValue && fieldValue !== "";
        });
    }

    isCompanySearchAvailable(): boolean {
        const iso3 = this.application?.company?.registeredAddress?.countryISO;
        if (!iso3) {
            return false;
        }
        const metadata = this.countryService.getCachedMetadataByIso3(iso3);
        return !!metadata.dotfileSearchEnabled;
    }

    canSwitchToCompanySearch(): boolean {
        return this.isCompanySearchAvailable() && this.companyInput !== 'search';
    }

    getCompletedSteps(): REGISTRATION_STEP_ID[] {
        return this.completedSteps.slice();
    }

    deactivateProgressBar(): void {
        this.navBarActivationObservable.next(false);
    }

    activateProgressBar(): void {
        this.navBarActivationObservable.next(true);
    }

    completeStep(completedStep: REGISTRATION_STEP_ID): void {
        this.logger.debug('StepService', 'completeStep', completedStep);
        this.completedSteps = [];
        for (let step of this.registrationFlow) {
            this.completedSteps.push(step.id);
            if (step.id === completedStep) break;
        }
        this.logger.debug('StepService', 'completeStep', this.completedSteps);
    }

    initRegistrationFlow(application: Application, complianceData = null): string {
        this.logger.debug('StepService', 'initApplication', application);
        this.setApplication(application);
        this.setComplianceData(complianceData);
        this.completedSteps = (application.webappCheckList?.split(';') || []) as REGISTRATION_STEP_ID[];
        return this.getNextIncompleteStep();
    }

    private getNextIncompleteStep() {
        this.logger.debug('StepService', 'goToNextIncompleteStep');
        const registrationFlow = this.getRegistrationFlowSnapshot();
        let nextIndex = registrationFlow.findIndex(s => s.active) + 1;
        let nextStep: RegistrationStep;
        while (nextIndex < registrationFlow.length && nextStep === undefined) {
            if (this.isStepComplete(registrationFlow[nextIndex].id)) {
                nextIndex++;
            } else {
                nextStep = registrationFlow[nextIndex];
            }
        }
        if (nextStep) {
            this.logger.debug('StepService', 'goToNextIncompleteStep', nextStep);
            this.lastNavigationDirection = NavigationDirection.FORWARD;
            return nextStep.linkRoute();
        } else {
            this.logger.error('StepService', 'goToNextIncompleteStep', 'This application should be completed');
        }
    }

    getRegistrationFlowSnapshot(): RegistrationStep[] {
        return [...this.registrationFlow];
    }

    private getFirstStep(): RegistrationStep {
        return this.registrationFlow[0];
    }

    getCompanySearchStep(): RegistrationStep {
        return this.companySearchStep;
    }

    getManualCompanyInputStep(): RegistrationStep {
        return this.companyIdentifiersStep;
    }

    getNextStep(): RegistrationStep {
        const activeIndex = this.registrationFlow.findIndex(s => s.active);
        if (activeIndex >= this.registrationFlow.length - 1) {
            return null;
        }
        this.logger.debug('StepService', 'getNextStep', this.registrationFlow[activeIndex + 1]);
        this.lastNavigationDirection = NavigationDirection.FORWARD;
        return this.registrationFlow[activeIndex + 1];
    }

    getPreviousStep(): RegistrationStep {
        const activeIndex = this.registrationFlow.findIndex(s => s.active);
        if (activeIndex <= 0) {
            return this.getFirstStep();
        }
        this.logger.debug('StepService', 'getPreviousStep', this.registrationFlow[activeIndex - 1]);
        this.lastNavigationDirection = NavigationDirection.BACKWARD;
        return this.registrationFlow[activeIndex - 1];
    }

    getLastNavigationDirection(): NavigationDirection {
        return this.lastNavigationDirection;
    }

    isStepComplete(step: REGISTRATION_STEP_ID): boolean {
        const status = this.application?.status;
        this.logger.debug('StepService', 'isStepComplete', step, `status: ${status}`, this.completedSteps.includes(step));
        if ([ApplicationStatus.Completed, ApplicationStatus.ReviewInProgress].includes(status) && step !== REGISTRATION_STEP_ID.LAST) {
            return true;
        }
        return this.completedSteps.includes(step);
    }

}
